/**
 * Page back-office : Audit des crédits par option × niveau × semestre.
 *
 * Objectif : chaque (option, niveau, semestre) doit totaliser 30 crédits.
 *
 * Sécurités :
 * - Affichage des matières ayant déjà des notes (verrou visuel)
 * - Backup JSON automatique côté serveur avant toute modification
 * - Bouton "Valider" désactivé si total ≠ 30 (forçage explicite possible)
 * - Distribution proportionnelle disponible mais NE modifie rien sans validation manuelle
 */
import { Fragment, useMemo, useState } from 'react';
import {
  Calculator, AlertTriangle, CheckCircle2, Sparkles, Lock,
  ChevronRight, X, Loader2, Search, Save, RefreshCw, Shield, Info, Layers,
} from 'lucide-react';
import {
  useAuditOverviewQuery, useAuditDetailQuery,
  useAuditDistribuerMutation, useAuditSaveMutation,
  useAuditSaveMultiMutation,
  type AuditGroupe, type AuditDetailResponse,
} from '../../services/auditCreditsApi';

const NIVEAUX = ['L1', 'L2', 'L3'];
const SEMESTRES = [1, 2, 3, 4, 5, 6];

type StatutFiltre = 'tous' | 'ok' | 'excedent' | 'deficit';

export default function AuditCredits() {
  const [niveau, setNiveau] = useState('');
  const [semestre, setSemestre] = useState<number | ''>('');
  const [option, setOption] = useState('');
  const [pole, setPole] = useState('');
  const [typeFormation, setTypeFormation] = useState<'' | 'FI' | 'CS'>('');
  const [statut, setStatut] = useState<StatutFiltre>('tous');
  const [selected, setSelected] = useState<AuditGroupe | null>(null);
  // Fix 2026-09-08 : sélection multi-options pour édition groupée
  const [multiSel, setMultiSel] = useState<Set<string>>(new Set());
  const [multiEdit, setMultiEdit] = useState<{
    options: string[]; niveau: string; semestre: number;
  } | null>(null);

  const { data, isFetching, refetch } = useAuditOverviewQuery(
    {
      niveau: niveau || undefined,
      semestre: typeof semestre === 'number' ? semestre : undefined,
      type_formation: typeFormation || undefined,
    },
  );

  // Liste unique des options + pôles disponibles pour les dropdowns
  const optionsDispo = useMemo(() => {
    const set = new Set<string>();
    (data?.results || []).forEach(i => set.add(i.option));
    return Array.from(set).sort();
  }, [data]);
  const polesDispo = useMemo(() => {
    const set = new Set<string>();
    (data?.results || []).forEach(i => { if (i.pole) set.add(i.pole); });
    return Array.from(set).sort();
  }, [data]);

  const filtered = useMemo(() => {
    let items = data?.results || [];
    if (statut !== 'tous') items = items.filter(i => i.ecart.statut === statut);
    if (option) items = items.filter(i => i.option === option);
    if (pole) items = items.filter(i => (i.pole || '') === pole);
    return items;
  }, [data, statut, option, pole]);

  // Clé unique pour un groupe (option+niveau+semestre) — pour la sélection multi
  const _key = (g: AuditGroupe) => `${g.option}|${g.niveau}|${g.semestre}`;

  // Sélection multi restreinte aux groupes du même (niveau, semestre)
  const multiSelGroups = useMemo(() => {
    return (data?.results || []).filter(g => multiSel.has(_key(g)));
  }, [data, multiSel]);

  const multiSelValide = useMemo(() => {
    if (multiSelGroups.length < 2) return { ok: false, motif: 'Cochez au moins 2 options' };
    const niv = new Set(multiSelGroups.map(g => g.niveau));
    const sem = new Set(multiSelGroups.map(g => g.semestre));
    if (niv.size > 1) return { ok: false, motif: 'Toutes les options doivent être du même niveau' };
    if (sem.size > 1) return { ok: false, motif: 'Toutes les options doivent être du même semestre' };
    return { ok: true, motif: '' };
  }, [multiSelGroups]);

  const toggleMulti = (g: AuditGroupe) => {
    const k = _key(g);
    setMultiSel(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <header className="mb-5 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Calculator size={22} className="text-blue-600 dark:text-blue-400" />
            Audit Crédits — Cible {data?.cible ?? 30} / semestre
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Vérification que chaque combinaison <strong>option × niveau × semestre</strong> totalise
            bien <strong>{data?.cible ?? 30} crédits</strong>. Ajustement manuel ou distribution
            proportionnelle avec backup automatique.
          </p>
        </div>
        <button onClick={() => refetch()} disabled={isFetching}
          className="px-3 py-2 text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg inline-flex items-center gap-1.5">
          <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} />
          {isFetching ? 'Chargement…' : 'Actualiser'}
        </button>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <Kpi label="Total groupes" valeur={data?.total_groupes ?? '—'} couleur="blue"
          icon={<Calculator size={14} />} />
        <Kpi label="Conformes (30)" valeur={data?.kpis.conformes ?? '—'} couleur="emerald"
          icon={<CheckCircle2 size={14} />} />
        <Kpi label="En déficit (< 30)" valeur={data?.kpis.deficit ?? '—'} couleur="amber"
          icon={<AlertTriangle size={14} />} />
        <Kpi label="Excédent (> 30)" valeur={data?.kpis.excedent ?? '—'} couleur="red"
          icon={<AlertTriangle size={14} />} />
      </div>

      {/* Alertes orphelines */}
      {data && data.nb_orphelines > 0 && (
        <div className="mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-sm text-amber-800 dark:text-amber-200 inline-flex items-start gap-2">
          <Info size={16} className="flex-shrink-0 mt-0.5" />
          <div>
            <strong>{data.nb_orphelines} matière(s) sans UE détectée(s)</strong> :
            ces matières existent mais ne sont liées à aucune UE, donc invisibles côté options.
            <details className="mt-1.5">
              <summary className="cursor-pointer text-xs underline">Voir la liste</summary>
              <ul className="mt-2 space-y-0.5 text-xs">
                {data.orphelines.map(o => (
                  <li key={o.id}>
                    <code className="bg-amber-100 dark:bg-amber-900/40 px-1 rounded">{o.code}</code>
                    {' '}<strong>{o.nom}</strong> — {o.niveau} S{o.semestre} ({o.credits} crédits)
                  </li>
                ))}
              </ul>
            </details>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select value={niveau} onChange={(e) => setNiveau(e.target.value)}
          className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
          <option value="">Tous niveaux</option>
          {NIVEAUX.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <select value={semestre} onChange={(e) => setSemestre(e.target.value ? parseInt(e.target.value) : '')}
          className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
          <option value="">Tous semestres</option>
          {SEMESTRES.map(s => <option key={s} value={s}>S{s}</option>)}
        </select>
        {/* Fix 2026-09-09 : filtre FI / CS */}
        <select value={typeFormation} onChange={(e) => setTypeFormation(e.target.value as any)}
          className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
          <option value="">FI + CS</option>
          <option value="FI">Formation Initiale (FI)</option>
          <option value="CS">Certificat Supplémentaire (CS)</option>
        </select>
        {/* Fix 2026-09-08 : filtre Pôle */}
        <select value={pole} onChange={(e) => setPole(e.target.value)}
          className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
          <option value="">Tous pôles</option>
          {polesDispo.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        {/* Fix 2026-09-08 : Option en liste déroulante (au lieu du champ texte) */}
        <select value={option} onChange={(e) => setOption(e.target.value)}
          className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 min-w-[220px]">
          <option value="">Toutes options ({optionsDispo.length})</option>
          {optionsDispo
            .filter(o => !pole || (data?.results || []).some(g => g.option === o && g.pole === pole))
            .map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded p-1">
          {(['tous', 'deficit', 'excedent', 'ok'] as StatutFiltre[]).map(s => (
            <button key={s} onClick={() => setStatut(s)}
              className={`px-3 py-1 text-xs rounded ${
                statut === s ? 'bg-blue-600 text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}>
              {s === 'tous' ? 'Tous' : s === 'ok' ? 'Conformes' : s === 'deficit' ? 'Déficits' : 'Excédents'}
            </button>
          ))}
        </div>
        {multiSel.size > 0 && (
          <div className="ml-auto inline-flex items-center gap-2">
            <span className="text-[11px] font-semibold text-blue-700 dark:text-blue-300">
              {multiSel.size} option(s) sélectionnée(s)
            </span>
            <button
              onClick={() => setMultiSel(new Set())}
              className="text-[10px] px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
            >Vider</button>
            <button
              onClick={() => {
                if (!multiSelValide.ok) { alert(multiSelValide.motif); return; }
                setMultiEdit({
                  options: multiSelGroups.map(g => g.option),
                  niveau: multiSelGroups[0].niveau,
                  semestre: multiSelGroups[0].semestre,
                });
              }}
              disabled={!multiSelValide.ok}
              title={multiSelValide.ok ? 'Éditer les crédits de toutes les options sélectionnées' : multiSelValide.motif}
              className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded inline-flex items-center gap-1.5"
            >
              <Layers size={12} />
              Édition groupée
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isFetching && !data ? (
          <div className="p-10 text-center text-gray-500">
            <Loader2 size={20} className="inline animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <CheckCircle2 size={36} strokeWidth={1.3} className="mx-auto mb-3 text-emerald-400" />
            {data?.kpis.conformes === data?.total_groupes
              ? 'Tous les groupes sont conformes (30 crédits) ✓'
              : 'Aucun groupe ne correspond aux filtres.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-2 py-2 w-8"></th>
                  <th className="text-left px-3 py-2">Option</th>
                  <th className="text-left px-3 py-2">Pôle</th>
                  <th className="text-center px-3 py-2">Niveau</th>
                  <th className="text-center px-3 py-2">Sem.</th>
                  <th className="text-center px-3 py-2">UEs</th>
                  <th className="text-center px-3 py-2">Matières</th>
                  <th className="text-right px-3 py-2">Total</th>
                  <th className="text-right px-3 py-2">Cible</th>
                  <th className="text-center px-3 py-2">Statut</th>
                  <th className="text-right px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {filtered.map((g, idx) => {
                  const k = _key(g);
                  const checked = multiSel.has(k);
                  return (
                    <tr key={`${g.option}-${g.niveau}-${g.semestre}-${idx}`}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${checked ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                      <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleMulti(g)}
                          className="w-4 h-4 accent-blue-600 cursor-pointer"
                          title="Cocher pour édition groupée"
                        />
                      </td>
                      <td className="px-3 py-2 text-gray-900 dark:text-white font-medium cursor-pointer"
                        onClick={() => setSelected(g)}>{g.option}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer"
                        onClick={() => setSelected(g)}>{g.pole || <span className="italic text-gray-400">—</span>}</td>
                      <td className="px-3 py-2 text-center text-xs text-gray-600 dark:text-gray-400 cursor-pointer"
                        onClick={() => setSelected(g)}>{g.niveau}</td>
                      <td className="px-3 py-2 text-center text-xs text-gray-600 dark:text-gray-400 cursor-pointer"
                        onClick={() => setSelected(g)}>S{g.semestre}</td>
                      <td className="px-3 py-2 text-center text-xs text-gray-600 dark:text-gray-400 cursor-pointer"
                        onClick={() => setSelected(g)}>{g.nb_ues}</td>
                      <td className="px-3 py-2 text-center text-xs text-gray-600 dark:text-gray-400 cursor-pointer"
                        onClick={() => setSelected(g)}>{g.nb_matieres}</td>
                      <td className={`px-3 py-2 text-right font-mono font-semibold cursor-pointer ${
                        g.ecart.statut === 'ok' ? 'text-emerald-600 dark:text-emerald-400'
                        : g.ecart.statut === 'deficit' ? 'text-amber-600 dark:text-amber-400'
                        : 'text-red-600 dark:text-red-400'
                      }`} onClick={() => setSelected(g)}>{g.total_credits}</td>
                      <td className="px-3 py-2 text-right text-xs text-gray-500 cursor-pointer"
                        onClick={() => setSelected(g)}>{data?.cible}</td>
                      <td className="px-3 py-2 text-center cursor-pointer" onClick={() => setSelected(g)}>
                        <EcartBadge ecart={g.ecart} />
                      </td>
                      <td className="px-3 py-2 text-right cursor-pointer" onClick={() => setSelected(g)}>
                        <ChevronRight size={14} className="inline text-gray-400" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <DetailDrawer
          option={selected.option} niveau={selected.niveau} semestre={selected.semestre}
          onClose={() => setSelected(null)}
        />
      )}

      {multiEdit && (
        <MultiEditDrawer
          options={multiEdit.options}
          niveau={multiEdit.niveau}
          semestre={multiEdit.semestre}
          onClose={() => setMultiEdit(null)}
          onSaved={() => { setMultiEdit(null); setMultiSel(new Set()); refetch(); }}
        />
      )}
    </div>
  );
}

function Kpi({ label, valeur, couleur, icon }: {
  label: string; valeur: number | string;
  couleur: 'blue' | 'emerald' | 'amber' | 'red';
  icon: React.ReactNode;
}) {
  const cls: Record<string, string> = {
    blue: 'text-blue-600 dark:text-blue-400',
    emerald: 'text-emerald-600 dark:text-emerald-400',
    amber: 'text-amber-600 dark:text-amber-400',
    red: 'text-red-600 dark:text-red-400',
  };
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5">
      <div className={`flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${cls[couleur]}`}>
        {icon} {label}
      </div>
      <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{valeur}</div>
    </div>
  );
}

function EcartBadge({ ecart }: { ecart: AuditGroupe['ecart'] }) {
  const cls = ecart.statut === 'ok'
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    : ecart.statut === 'deficit'
      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
      : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
  return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${cls}`}>{ecart.label}</span>;
}

// ──────────────────────────────────────────────────────────────────────────
// DRAWER DÉTAIL — édition crédits avec backup et distribution proportionnelle
// ──────────────────────────────────────────────────────────────────────────

function DetailDrawer({ option, niveau, semestre, onClose }: {
  option: string; niveau: string; semestre: number; onClose: () => void;
}) {
  const { data, isFetching } = useAuditDetailQuery({ option, niveau, semestre });
  const [editedCredits, setEditedCredits] = useState<Record<number, number>>({});
  const [distribuer, { isLoading: distribLoading }] = useAuditDistribuerMutation();
  const [save, { isLoading: saving }] = useAuditSaveMutation();
  const [forceModal, setForceModal] = useState(false);

  // Total live (avec les modifications)
  const totalLive = useMemo(() => {
    if (!data) return 0;
    let total = 0;
    for (const ue of data.ues) {
      for (const m of ue.matieres) {
        total += editedCredits[m.id] !== undefined ? editedCredits[m.id] : m.credits;
      }
    }
    return Math.round(total * 10) / 10;
  }, [data, editedCredits]);

  const modifications = useMemo(() => {
    if (!data) return [];
    const mods: { id: number; credits: number }[] = [];
    for (const ue of data.ues) {
      for (const m of ue.matieres) {
        const nouv = editedCredits[m.id];
        if (nouv !== undefined && nouv !== m.credits) {
          mods.push({ id: m.id, credits: nouv });
        }
      }
    }
    return mods;
  }, [data, editedCredits]);

  const cible = data?.cible ?? 30;
  const ecart = totalLive - cible;
  const isOk = Math.abs(ecart) < 0.01;
  const hasModif = modifications.length > 0;

  const setCredit = (id: number, v: number) => {
    setEditedCredits(prev => ({ ...prev, [id]: v }));
  };

  const handleDistribuer = async () => {
    if (!data) return;
    try {
      const r = await distribuer({ option, niveau, semestre, exclure_matieres_avec_notes: true }).unwrap();
      // Pré-remplit l'édition avec les modifications suggérées
      const next: Record<number, number> = {};
      for (const mod of r.modifications) next[mod.id] = mod.credits_apres;
      setEditedCredits(next);
    } catch (e: any) {
      alert(e?.data?.detail || e?.data?.message || 'Erreur distribution');
    }
  };

  const handleSave = async (force = false, raison = '') => {
    if (!hasModif) {
      alert("Aucune modification à enregistrer.");
      return;
    }
    try {
      const r = await save({
        option, niveau, semestre, modifications, force, raison,
      }).unwrap();
      alert(
        `Enregistré ✓\nMatières modifiées : ${r.nb_modifiees}` +
        (r.backup_path ? `\nBackup : ${r.backup_path.split('/').pop()}` : ''),
      );
      setEditedCredits({});
      setForceModal(false);
    } catch (e: any) {
      // Fix 2026-09-09 : afficher le message d'erreur détaillé (fetchBase
      // wrap l'erreur dans data.detail OU data.error OU status text).
      const msg = e?.data?.detail
        || e?.data?.error
        || e?.data?.message
        || (typeof e?.data === 'string' ? e.data : '')
        || e?.error
        || `Erreur sauvegarde (HTTP ${e?.status || '?'})`;
      alert(msg);
      // eslint-disable-next-line no-console
      console.error('audit-credits/save erreur', e);
    }
  };

  const onAttemptSave = () => {
    if (isOk) handleSave(false, '');
    else setForceModal(true);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose}>
      <div className="absolute inset-y-0 right-0 w-full max-w-3xl bg-white dark:bg-gray-900 shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>

        <header className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-5 py-3 flex items-center justify-between z-10">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              {option}
            </h2>
            <div className="text-xs text-gray-500">{niveau} · Semestre {semestre}</div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </header>

        {/* Total live */}
        <div className={`mx-5 my-3 p-4 rounded-lg border ${
          isOk ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
          : ecart < 0 ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
          : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
        }`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400">
                Total crédits {hasModif && '(prévisualisation)'}
              </div>
              <div className={`text-3xl font-bold font-mono ${
                isOk ? 'text-emerald-600 dark:text-emerald-400'
                : ecart < 0 ? 'text-amber-600 dark:text-amber-400'
                : 'text-red-600 dark:text-red-400'
              }`}>{totalLive} <span className="text-sm font-normal text-gray-500">/ {cible}</span></div>
            </div>
            <div className="text-right">
              {isOk ? (
                <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
                  <CheckCircle2 size={14} /> Équilibre atteint
                </div>
              ) : (
                <div className={`text-sm font-semibold ${ecart < 0 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'} inline-flex items-center gap-1`}>
                  <AlertTriangle size={14} />
                  {ecart > 0 ? '+' : ''}{ecart.toFixed(1)} crédit(s)
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mx-5 mb-3 flex flex-wrap gap-2">
          <button onClick={handleDistribuer} disabled={distribLoading || isOk}
            className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded inline-flex items-center gap-1.5">
            {distribLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            Distribuer proportionnellement
          </button>
          {hasModif && (
            <button onClick={() => setEditedCredits({})}
              className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded inline-flex items-center gap-1.5">
              Réinitialiser les modifications
            </button>
          )}
          <div className="ml-auto inline-flex items-center gap-1 text-[10px] text-gray-500">
            <Shield size={11} /> Backup JSON automatique avant sauvegarde
          </div>
        </div>

        {/* Liste UE/matières */}
        <div className="px-5 pb-20">
          {isFetching && !data ? (
            <div className="py-10 text-center text-gray-500">
              <Loader2 size={20} className="inline animate-spin" />
            </div>
          ) : data ? (
            <div className="space-y-3">
              {data.ues.map(ue => {
                const sousTotal = ue.matieres.reduce((acc, m) => {
                  const v = editedCredits[m.id] !== undefined ? editedCredits[m.id] : m.credits;
                  return acc + v;
                }, 0);
                return (
                  <section key={ue.ue_id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <header className="bg-gray-50 dark:bg-gray-800 px-3 py-2 flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="text-xs font-mono font-semibold text-gray-600 dark:text-gray-400">{ue.ue_code}</div>
                        <div className="text-sm text-gray-900 dark:text-white truncate">{ue.ue_nom}</div>
                      </div>
                      <div className="text-sm font-mono font-semibold text-gray-700 dark:text-gray-300">
                        {Math.round(sousTotal * 10) / 10}
                      </div>
                    </header>
                    <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                      {ue.matieres.map(m => {
                        const editedValue = editedCredits[m.id];
                        const currentValue = editedValue !== undefined ? editedValue : m.credits;
                        const modified = editedValue !== undefined && editedValue !== m.credits;
                        return (
                          <li key={m.id} className={`px-3 py-2 flex items-center gap-3 ${modified ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-gray-900 dark:text-white truncate">{m.nom_matiere}</div>
                              <div className="text-[10px] text-gray-500 font-mono">{m.code_matiere}</div>
                            </div>
                            {m.a_des_notes && (
                              <div className="text-[10px] text-amber-700 dark:text-amber-400 inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/40 rounded"
                                title={`${m.nb_notes_devoir} devoir(s) + ${m.nb_notes_examen} examen(s) déjà saisis`}>
                                <Lock size={10} /> {m.nb_notes_devoir}D / {m.nb_notes_examen}E
                              </div>
                            )}
                            <input
                              type="number" min={0.5} max={10} step={0.5}
                              value={currentValue}
                              onChange={(e) => setCredit(m.id, parseFloat(e.target.value) || 0)}
                              className={`w-16 px-2 py-1 text-sm text-right font-mono border rounded ${
                                modified ? 'border-blue-400 dark:border-blue-600 bg-white dark:bg-gray-700' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                              } text-gray-900 dark:text-gray-100`}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>
          ) : null}
        </div>

        {/* Footer sticky */}
        <footer className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-5 py-3 flex items-center justify-between gap-2">
          <div className="text-xs text-gray-500">
            {hasModif ? `${modifications.length} modification(s) en attente` : 'Aucune modification'}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose}
              className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded">
              Fermer
            </button>
            <button onClick={onAttemptSave} disabled={!hasModif || saving}
              className={`px-4 py-1.5 text-sm font-semibold rounded inline-flex items-center gap-1.5 disabled:opacity-50 ${
                isOk ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'bg-amber-600 hover:bg-amber-700 text-white'
              }`}>
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              {isOk ? 'Valider' : 'Forcer la sauvegarde'}
            </button>
          </div>
        </footer>

        {forceModal && data && (
          <ForceModal
            totalActuel={totalLive} cible={cible}
            loading={saving}
            onCancel={() => setForceModal(false)}
            onConfirm={(raison) => handleSave(true, raison)} />
        )}
      </div>
    </div>
  );
}

function ForceModal({ totalActuel, cible, loading, onCancel, onConfirm }: {
  totalActuel: number; cible: number; loading: boolean;
  onCancel: () => void; onConfirm: (raison: string) => void;
}) {
  const [raison, setRaison] = useState('');
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-base font-semibold text-amber-700 dark:text-amber-400 inline-flex items-center gap-2">
            <AlertTriangle size={16} /> Total ≠ {cible}
          </h3>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
            <X size={18} />
          </button>
        </header>
        <div className="p-5 space-y-3">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Le total des crédits après modification serait <strong className="font-mono">{totalActuel}</strong>,
            et non <strong>{cible}</strong>. Indiquez une raison pour forcer la sauvegarde.
          </p>
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Raison de la dérogation (logs) *
            </label>
            <textarea value={raison} onChange={(e) => setRaison(e.target.value)}
              rows={3} maxLength={300}
              placeholder="Ex : Décision jury de délibération du 26/05/26 — accord exceptionnel pour…"
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
            <p className="text-[10px] text-gray-500 mt-0.5">Minimum 5 caractères</p>
          </div>
        </div>
        <footer className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button onClick={onCancel}
            className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded">
            Annuler
          </button>
          <button onClick={() => onConfirm(raison)} disabled={loading || raison.trim().length < 5}
            className="px-4 py-1.5 text-sm bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-semibold rounded inline-flex items-center gap-1.5">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Forcer
          </button>
        </footer>
      </div>
    </div>
  );
}

// Fix 2026-09-08 : Édition groupée multi-options
// Charge le détail de la 1re option comme référence, permet de modifier
// les crédits par NOM DE MATIÈRE. La sauvegarde applique le nouveau
// crédit à toutes les matières homonymes présentes dans chaque option.
function MultiEditDrawer({ options, niveau, semestre, onClose, onSaved }: {
  options: string[]; niveau: string; semestre: number;
  onClose: () => void; onSaved: () => void;
}) {
  const optionRef = options[0];
  const { data, isFetching } = useAuditDetailQuery({ option: optionRef, niveau, semestre });
  const [saveMulti, { isLoading: saving }] = useAuditSaveMultiMutation();
  const [editedByNom, setEditedByNom] = useState<Record<string, number>>({});
  const [forceMode, setForceMode] = useState(false);
  const [raison, setRaison] = useState('');

  const totalLive = useMemo(() => {
    if (!data) return 0;
    let total = 0;
    for (const ue of data.ues) {
      for (const m of ue.matieres) {
        const nom = m.nom_matiere;
        total += editedByNom[nom] !== undefined ? editedByNom[nom] : m.credits;
      }
    }
    return Math.round(total * 10) / 10;
  }, [data, editedByNom]);

  const cible = data?.cible ?? 30;
  const ecart = totalLive - cible;
  const isOk = Math.abs(ecart) < 0.01;

  const modifications = useMemo(() => {
    if (!data) return [];
    const mods: { nom_matiere: string; credits: number }[] = [];
    for (const ue of data.ues) {
      for (const m of ue.matieres) {
        const nouv = editedByNom[m.nom_matiere];
        if (nouv !== undefined && nouv !== m.credits) {
          mods.push({ nom_matiere: m.nom_matiere, credits: nouv });
        }
      }
    }
    return mods;
  }, [data, editedByNom]);
  const hasModif = modifications.length > 0;

  const handleSave = async () => {
    if (!hasModif) { alert('Aucune modification'); return; }
    if (!isOk && !forceMode) {
      setForceMode(true);
      return;
    }
    try {
      const r = await saveMulti({
        options, niveau, semestre,
        modifications_par_nom: modifications,
        force: !isOk, raison: raison || undefined,
      }).unwrap();
      const detail = r.resultats.map(x =>
        `  · ${x.option} : ${x.nb_modifiees} modif${x.skipped ? ' (SKIPPÉ: ' + (x.motif || '') + ')' : ''}`
      ).join('\n');
      alert(`Édition groupée terminée ✓\n${r.total_modifiees} matières modifiées au total\n\n${detail}`);
      onSaved();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur sauvegarde multi');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose}>
      <div className="absolute inset-y-0 right-0 w-full max-w-3xl bg-white dark:bg-gray-900 shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-5 py-3 flex items-center justify-between z-10">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Layers size={16} className="text-blue-600" />
              Édition groupée — {options.length} options
            </h2>
            <div className="text-xs text-gray-500 mt-0.5">
              {niveau} · Semestre {semestre} · Modif s'applique aux matières homonymes de chaque option
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              {options.map(o => (
                <span key={o} className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                  {o}
                </span>
              ))}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </header>

        {/* Total live */}
        <div className={`mx-5 my-3 p-3 rounded-lg border ${
          isOk ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
          : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
        }`}>
          <div className="text-[10px] uppercase font-semibold text-gray-500 dark:text-gray-400">
            Total crédits {hasModif && '(prévisualisation)'}
          </div>
          <div className={`text-3xl font-bold font-mono ${
            isOk ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'
          }`}>
            {totalLive} <span className="text-sm font-normal text-gray-500">/ {cible}</span>
          </div>
        </div>

        {/* Matières éditables (par nom, référence = 1re option) */}
        <div className="px-5 pb-24">
          {isFetching || !data ? (
            <div className="py-10 text-center text-gray-500"><Loader2 size={20} className="inline animate-spin" /></div>
          ) : (
            <div className="space-y-3">
              {data.ues.map(ue => (
                <section key={ue.ue_id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <header className="bg-gray-50 dark:bg-gray-800 px-3 py-2">
                    <div className="text-xs font-mono font-semibold text-gray-600 dark:text-gray-400">{ue.ue_code}</div>
                    <div className="text-sm text-gray-900 dark:text-white truncate">{ue.ue_nom}</div>
                  </header>
                  <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                    {ue.matieres.map(m => {
                      const editedValue = editedByNom[m.nom_matiere];
                      const currentValue = editedValue !== undefined ? editedValue : m.credits;
                      const modified = editedValue !== undefined && editedValue !== m.credits;
                      return (
                        <li key={m.id} className={`px-3 py-2 flex items-center gap-3 ${modified ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-mono text-gray-500">{m.code_matiere}</div>
                            <div className="text-sm text-gray-800 dark:text-gray-200 truncate">{m.nom_matiere}</div>
                          </div>
                          <input
                            type="number" step="0.5" min="0" max="10"
                            value={currentValue}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              setEditedByNom(prev => ({ ...prev, [m.nom_matiere]: isNaN(v) ? m.credits : v }));
                            }}
                            className="w-20 px-2 py-1 text-sm text-right font-mono border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          />
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>

        {forceMode && (
          <div className="mx-5 my-3 p-3 bg-amber-50 border border-amber-300 rounded text-sm">
            <div className="font-semibold mb-2 text-amber-800">Total = {totalLive} ≠ {cible} — forcer la sauvegarde ?</div>
            <input
              type="text" value={raison} onChange={(e) => setRaison(e.target.value)}
              placeholder="Raison (obligatoire, ≥5 caractères)"
              className="w-full px-2 py-1 text-sm border border-amber-300 rounded"
            />
          </div>
        )}

        <footer className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-5 py-3 flex items-center justify-between gap-2">
          <div className="text-xs text-gray-500">
            {hasModif ? `${modifications.length} modification(s) × ${options.length} options` : 'Aucune modification'}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose}
              className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded">
              Fermer
            </button>
            <button onClick={handleSave} disabled={!hasModif || saving || (forceMode && raison.trim().length < 5)}
              className={`px-4 py-1.5 text-sm font-semibold rounded inline-flex items-center gap-1.5 disabled:opacity-50 ${
                isOk ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'bg-amber-600 hover:bg-amber-700 text-white'
              }`}>
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              {isOk ? `Valider (${options.length} options)` : forceMode ? 'Forcer' : 'Continuer…'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
